/**
 * Who the offline data on this device belongs to.
 *
 * The local store — including the outbox of changes not yet synced — belongs
 * to one person. Their queued changes can only be sent under their own
 * sign-in (the server attributes and authorises each change by the caller),
 * and when someone else signs in OfflineProvider clears the store. The login
 * screen reads this to warn before that happens, so a second person signing in
 * on a shared device doesn't silently erase the first person's unsynced work.
 */
import type { User } from "@/types/api";

const OWNER_IDENTITY_KEY = "offline:owner";

export interface OfflineOwner {
  id: number;
  name: string;
  email: string | null;
  loginId: string | null;
}

export function rememberOfflineOwner(u: User): void {
  const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email || u.rank || u.loginId || "Another user";
  const owner: OfflineOwner = { id: u.id, name, email: u.email, loginId: u.loginId };
  try { localStorage.setItem(OWNER_IDENTITY_KEY, JSON.stringify(owner)); } catch { /* storage disabled */ }
}

export function readOfflineOwner(): OfflineOwner | null {
  try {
    const s = localStorage.getItem(OWNER_IDENTITY_KEY);
    return s ? (JSON.parse(s) as OfflineOwner) : null;
  } catch {
    return null;
  }
}

export function forgetOfflineOwner(): void {
  try { localStorage.removeItem(OWNER_IDENTITY_KEY); } catch { /* storage disabled */ }
}

/** Whether `identifier` (what was typed on the login screen) is the owner's. */
export function isOfflineOwner(owner: OfflineOwner, identifier: string): boolean {
  const typed = identifier.trim().toLowerCase();
  return [owner.email, owner.loginId].some((x) => !!x && x.toLowerCase() === typed);
}
